import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2, ShieldAlert, Eye, EyeOff } from "lucide-react";

const dashboardFor = (role) => (role === "seller" || role === "both" ? "/dashboard/seller" : "/dashboard/buyer");

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = (params.get("token") || "").trim();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (pw !== confirm) { toast.error("Passwords don't match"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password: pw });
            toast.success("Password reset — you're now signed in");
            await refreshUser();
            navigate(dashboardFor(data.user?.role), { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reset your password. The link may have expired.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-[80vh] max-w-2xl items-center px-4 py-12 sm:px-6">
            <Card
                data-testid="reset-password-card"
                className="noise-overlay relative w-full overflow-hidden border-border bg-card p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-12"
            >
                <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-40 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(240,180,41,0.16)_0%,rgba(240,180,41,0)_70%)]" />

                {!token ? (
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/15 text-destructive ring-1 ring-destructive/30">
                            <ShieldAlert className="h-8 w-8" aria-hidden />
                        </span>
                        <h1 data-testid="reset-invalid-title" className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                            Invalid reset link
                        </h1>
                        <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                            This password reset link is missing or malformed. Please request a new one.
                        </p>
                        <Button
                            data-testid="reset-request-new-btn"
                            size="lg"
                            className="mt-8 w-full max-w-sm rounded-[10px] font-semibold"
                            onClick={() => navigate("/account/forgot-password")}
                        >
                            Request a new link <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <div className="relative z-10">
                        <div className="flex flex-col items-center text-center">
                            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brass/15 text-brass ring-1 ring-brass/30">
                                <Lock className="h-8 w-8" aria-hidden />
                            </span>
                            <h1 data-testid="reset-password-title" className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                                Set a new password
                            </h1>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                                Choose a strong password you don&rsquo;t use anywhere else. You&rsquo;ll be signed in automatically.
                            </p>
                        </div>

                        <form onSubmit={submit} className="mx-auto mt-8 w-full max-w-sm space-y-5">
                            <div className="space-y-2.5">
                                <Label htmlFor="password">New password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        data-testid="reset-password-input"
                                        type={show ? "text" : "password"}
                                        value={pw}
                                        onChange={(e) => setPw(e.target.value)}
                                        minLength={6}
                                        placeholder="At least 6 characters"
                                        required
                                        className="pr-11"
                                    />
                                    <button
                                        type="button"
                                        data-testid="reset-toggle-visibility"
                                        onClick={() => setShow((s) => !s)}
                                        aria-label={show ? "Hide password" : "Show password"}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2.5">
                                <Label htmlFor="confirm">Confirm new password</Label>
                                <Input
                                    id="confirm"
                                    data-testid="reset-confirm-input"
                                    type={show ? "text" : "password"}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    minLength={6}
                                    placeholder="Re-enter your password"
                                    required
                                />
                                {confirm.length > 0 && confirm !== pw && (
                                    <p data-testid="reset-mismatch" className="text-xs text-destructive">Passwords don&rsquo;t match.</p>
                                )}
                            </div>
                            <Button
                                type="submit"
                                size="lg"
                                disabled={loading}
                                data-testid="reset-submit-btn"
                                className="w-full rounded-[10px] font-semibold"
                            >
                                {loading ? (
                                    <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Resetting&hellip;</>
                                ) : (
                                    <>Reset password <ArrowRight className="ml-1.5 h-4 w-4" /></>
                                )}
                            </Button>
                        </form>

                        <p className="mt-8 text-center text-sm text-muted-foreground">
                            Remembered it?{" "}
                            <Link to="/login" data-testid="reset-back-link" className="font-medium text-electric hover:underline">Back to login</Link>
                        </p>
                    </div>
                )}
            </Card>
        </div>
    );
}
